"use client";

import {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  startTransition,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
// Locale-aware Link keeps /partner/:slug under the current locale prefix.
import { Link } from "@/i18n/navigation";
// Context links (breadcrumb, city landings) carry locale-prefixed paths already.
import NextLink from "next/link";
import { cn } from "@/lib/utils";
import { mergeTimeslotMatrixConfig } from "@/lib/utils/timeslot-matrix";
import { mergeDailyMatrixConfig } from "@/lib/utils/daily-matrix";
import type {
  ProductDetail,
  ProductRentalBlock,
  BranchOperationBlock,
  ReviewStats,
} from "@/types";
import { Star, MessageCircle, Heart, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
// Direct imports for above-fold components (better tree-shaking)
import ProductImageGallery from "@/components/product-detail/ProductImageGallery";
import { type CartItem } from "@/components/product-detail/BookingCard";
import QueryAwareBookingCard from "@/components/product-detail/QueryAwareBookingCard";
import PartnerInfoDetail from "@/components/product-detail/PartnerInfoDetail";
import PricingInfo from "@/components/product-detail/PricingInfo";
import BusinessInfoModal from "@/components/product-detail/BusinessInfoModal";
import BusinessInfoSection from "@/components/product-detail/BusinessInfoSection";
import SimilarCategoryProducts from "@/components/SimilarCategoryProducts";
import PartnerProducts from "@/components/PartnerProducts";
import ProductUsageMethods from "@/components/product-detail/ProductUsageMethods";
import ProductDescription from "@/components/product-detail/ProductDescription";
import BikeSpecTable from "@/components/product-detail/BikeSpecTable";
import { ProductCouponBanner } from "@/components/product-detail/ProductCouponBanner";
import ProductAdditionalInfo from "@/components/product-detail/ProductAdditionalInfo";
import ProductNotices from "@/components/product-detail/ProductNotices";
import ShareDialog from "@/components/product-detail/ShareDialog";
import { ShareNetworkIcon } from "@phosphor-icons/react";
import ProductFloatingActions from "@/components/product-detail/ProductFloatingActions";
import { buildBigMapHref, isCoverageMode } from "@/lib/branches/coverage";
import { OptimizedImage } from "@/components/ui/optimized-image";
import ProductBreadcrumbs from "@/components/product-detail/ProductBreadcrumbs";
import ProductLocationHeader from "@/components/product-detail/ProductLocationHeader";
import type { ProductContextLinks } from "@/lib/seo/product-breadcrumb";

// Dynamic imports for below-fold components (code-splitting)
// SSR enabled: 서버에서 미리 렌더링되므로 loading skeleton 불필요
const ProductReviews = dynamic(
  () => import("@/components/product-detail/ProductReviews"),
  { ssr: true },
);
const ProductQnA = dynamic(
  () => import("@/components/product-detail/ProductQnA"),
  { ssr: true },
);
const ProductFAQs = dynamic(
  () => import("@/components/product-detail/ProductFAQs"),
  { ssr: true },
);
const CancellationPolicy = dynamic(
  () => import("@/components/product-detail/CancellationPolicy"),
  { ssr: true },
);
// ProductBranches는 Google Maps JS API를 transitively 로드하므로 초기 번들에서 분리.
// ssr: false + 고정 높이 placeholder로 CLS 방지.
const ProductBranches = dynamic(
  () => import("@/components/product-detail/ProductBranches"),
  {
    ssr: false,
    loading: () => (
      <div className="h-62.5 rounded-xl bg-gray-100 animate-pulse" />
    ),
  },
);

import { useTranslations } from "next-intl";
import { translateCityNames, translateCityName } from "@/config/locations";
import { rankCityNamesByCount } from "@/lib/utils/format-cities";
import AuthModal from "@/components/AuthModal";
import WishlistButton from "@/components/WishlistButton";
import { useAuth } from "@/contexts/AuthContext";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { useStickyScroll } from "@/hooks/useStickyScroll";
import { readSafeAreaInsetTop } from "@/lib/utils/safe-area";
import { recordProductView } from "@/app/actions/product-views";
import { recordAnalyticsEvent } from "@/app/actions/analytics";
import { getSessionId } from "@/lib/analytics/session";
import {
  getDailyRateForDays,
  getBaseDailyRate,
  calculateRentalDays,
  calculateRentalPrice,
} from "@/lib/utils/pricing";
import {
  buildCheckoutData,
  saveAndNavigateToCheckout,
} from "@/lib/utils/checkout";
// WishlistButton handles its own API calls now
import { useToast } from "@/hooks/use-toast";
import { getOrCreateInquiryRoom } from "@/app/actions/chat";
import { useCurrency } from "@/contexts/CurrencyContext";
import type { SupportedCurrency } from "@/lib/utils/currency";
import type { ConvertedPrice } from "@/lib/api/products/detail";
import type { AvailableCoupon } from "@/app/actions/coupons";
import { hasBikeSpecs } from "@/lib/mobility/bike-specs";
import { formatKeyFacts, type KeyFact } from "@/lib/product-facts/key-facts";
import type { ModelSpecFacts } from "@/lib/mobility/model-spec-format";
import {
  disputedComponentNames,
  parseComponentSections,
} from "@/lib/product-facts/component-sections";
import ProductKeyFacts from "@/components/product-detail/ProductKeyFacts";
import type { Locale } from "@/i18n/routing";

// Generate time options (00:00 to 23:30 in 30 min intervals)
const generateTimeOptions = () => {
  const times: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hourStr = hour.toString().padStart(2, "0");
      const minuteStr = minute.toString().padStart(2, "0");
      times.push(`${hourStr}:${minuteStr}`);
    }
  }
  return times;
};

interface ProductDetailClientProps {
  productId: string;
  locale: string;
  initialProduct: ProductDetail;
  convertedPrice?: ConvertedPrice;
  initialProductBlocks: ProductRentalBlock[];
  initialBranchBlocks: BranchOperationBlock[];
  initialReviewStats: ReviewStats;
  /** 전체 리뷰 수(집계) — 목록은 클라이언트에서 차단 목록을 적용해 받는다 */
  initialReviewsTotal: number;
  initialCoupons: AvailableCoupon[];
  /**
   * Server-rendered detail page body (parses TipTap atom-block placeholders
   * into live product/partner cards). Provided when `detail_page_html` is
   * present, so the client can embed it directly without re-parsing.
   */
  detailPageBody?: ReactNode;
  /**
   * Breadcrumb trail, indexed city × category landings and station chips —
   * computed on the server (page.tsx) from the same coverage the sitemap uses.
   */
  contextLinks?: ProductContextLinks;
  /**
   * "핵심 정보" facts (`buildKeyFacts`, server). Formatted here so prices follow
   * the viewer's currency; bikes add them to the vehicle table, other
   * categories get one small table.
   */
  keyFacts?: KeyFact[];
  /**
   * Official model specs (`modelSpecsForProduct`, server — the data file stays
   * out of the client bundle). Rows in the vehicle table.
   */
  modelSpecs?: ModelSpecFacts | null;
  /** 차량 정보 "차종" 값 — 모델 허브의 그 언어 이름(+ 사양), server. 없으면 원문 */
  bikeModelName?: string | null;
}

export default function ProductDetailClient({
  productId,
  locale,
  initialProduct,
  convertedPrice,
  initialProductBlocks,
  initialBranchBlocks,
  initialReviewStats,
  initialReviewsTotal,
  initialCoupons,
  detailPageBody,
  contextLinks,
  keyFacts = [],
  modelSpecs = null,
  bikeModelName = null,
}: ProductDetailClientProps) {
  const router = useRouter();
  const t = useTranslations("product");
  const tCommon = useTranslations("common");
  // 서버에서 받은 데이터를 직접 사용 (불필요한 state 제거)
  const product = initialProduct;

  // 검색 페이지에서 전달된 날짜·지점 쿼리(?checkin/checkout/branch)는
  // QueryAwareBookingCard 가 읽는다 — 여기서 useSearchParams 를 쓰면 정적 렌더(ISR)
  // 에서 상세 본문 전체가 클라이언트 렌더로 넘어간다.
  const productBlocks = initialProductBlocks;
  const branchBlocks = initialBranchBlocks;

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [activeSection, setActiveSection] = useState("notices");
  // 로그인 상태는 AuthProvider 에서 — 캐시된 페이지라 서버가 사용자를 모른다.
  const { status: authStatus, user, refresh: refreshAuth } = useAuth();
  const authChecked = authStatus !== "loading";
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  // 매장 정보 모달 상태
  const [businessInfoModalOpen, setBusinessInfoModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // 지점이 많은 상품(캠핑 장비 113곳 등) — 위치 섹션·플로팅 지도 버튼이 같은 검색 링크를 쓴다.
  const bigMapHref = isCoverageMode(product.branches)
    ? buildBigMapHref(
        locale,
        product.partner?.slug,
        product.category_code,
        product.id,
      )
    : null;
  const { toast } = useToast();
  const { formatFromSource, formatWithCodeFromSource } = useCurrency();

  // 상품의 기준 통화 (DB에 저장된 통화)
  const sourceCurrency: SupportedCurrency = product?.currency || "USD";

  // 핵심 정보 행 — 값은 서버가 DB 에서 고른 사실, 가격만 보는 사람 통화로.
  const tKeyFacts = useTranslations("product.keyFacts");
  const keyFactRows = useMemo(
    () =>
      formatKeyFacts(
        keyFacts,
        tKeyFacts,
        (amount) => formatWithCodeFromSource(amount, sourceCurrency),
        locale,
      ),
    [keyFacts, tKeyFacts, formatWithCodeFromSource, sourceCurrency, locale],
  );
  // 계절별 구성품(hinata) — 상세 HTML 에는 그림으로만 있어 텍스트로 함께 보인다.
  const componentSections = useMemo(
    () =>
      parseComponentSections(
        product.detail_sections,
        disputedComponentNames(product.specifications),
      ),
    [product.detail_sections, product.specifications],
  );

  const hostInfoDetailRef = useRef<HTMLDivElement>(null);
  const noticesRef = useRef<HTMLDivElement>(null);
  const bikeSpecsRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const qnaRef = useRef<HTMLDivElement>(null);
  const cancellationRef = useRef<HTMLDivElement>(null);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Smart sticky scroll for BookingCard - scrolls with page when content is taller than viewport
  const { containerRef: bookingCardRef, style: bookingCardStyle } =
    useStickyScroll({
      topOffset: 144, // navbar (80px) + sticky nav (48px) + gap (16px)
      bottomOffset: 48,
    });

  // Memoize time options to prevent recreation on every render
  const timeOptions = useMemo(() => generateTimeOptions(), []);

  // Region line under the title: branch regions by branch count — cities with
  // an indexed city × category landing as links, the rest as text; 3 shown +
  // "외 N곳".
  const cityLandings = contextLinks?.cityLandings;
  const regionLine = useMemo(() => {
    const lang = locale as Locale;
    const translated =
      product?.branches && product.branches.length > 0
        ? translateCityNames(
            product.branches.map((b) => b.city).filter(Boolean),
            lang,
          )
        : product?.primary_city
          ? [translateCityName(product.primary_city, lang)]
          : [];
    const linked = (cityLandings ?? []).map((l) => ({
      name: l.cityName,
      href: l.path as string | undefined,
      count: l.count,
    }));
    // "도쿄" (landing) also covers "도쿄도" / "東京都" (branch prefecture).
    const covered = (name: string) =>
      linked.some(
        (l) => l.name === name || name.startsWith(l.name) || l.name.startsWith(name),
      );
    // dedup AFTER translation: different raw values (e.g. "인천" vs "Incheon")
    // can normalize to the same display name and must collapse to one.
    // Ranked by branch count (ties: first seen) — branches arrive in
    // registration order, which put "아이치현·이바라키현·기후현" (2·10·4 campsites)
    // ahead of 야마나시현 (19) on hinata sets.
    const rest = rankCityNamesByCount(translated)
      .filter((city) => !covered(city.name))
      .map((city) => ({ name: city.name, href: undefined as string | undefined, count: city.count }));
    // Linked landings and plain regions in one order, most branches first
    // (stable sort: a linked city wins a tie).
    const all = [...linked, ...rest].sort((a, b) => b.count - a.count);
    // 60개 도시짜리 상품이 헤더 한 줄을 통째로 밀어내므로 3곳 + "외 N곳" 상한.
    return {
      shown: all.slice(0, 3),
      more:
        all.length > 3
          ? tCommon("moreCities", { count: all.length - 3 })
          : null,
    };
  }, [product, locale, tCommon, cityLandings]);
  const hasRegionLine = regionLine.shown.length > 0;

  // Check if sections have content
  const hasNotices = !!(
    product?.partner_notices?.length || product?.product_notices?.length
  );
  const hasDescription = !!(
    product?.description || product?.detail_sections?.length
  );
  // 차량 정보 표 — 오토바이 상품(`specifications.kind === "bike"`)에서만.
  const hasBikeSpecTable = hasBikeSpecs(product?.specifications);
  const hasBranches = !!product?.branches?.length;
  const hasAdditionalInfo = !!(
    product?.inclusions?.length ||
    product?.exclusions?.length ||
    product?.notes?.length ||
    product?.requirements?.length ||
    product?.additional_info?.included_items?.length ||
    product?.additional_info?.precautions?.length ||
    product?.additional_info?.required_notices?.length ||
    product?.faqs?.length
  );
  const hasCancellationPolicy = !!product?.partner?.cancellation_refund_policy;

  // Map section id → scroll target ref. Kept out of `navTabs`/useMemo so refs
  // are never read during render (react-hooks/refs); resolved only inside the
  // click handler below.
  const sectionRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
    notices: noticesRef,
    bikeSpecs: bikeSpecsRef,
    description: descriptionRef,
    location: locationRef,
    info: infoRef,
    cancellation: cancellationRef,
    partner: hostInfoDetailRef,
  };

  // Tab navigation items - 순서: 공지사항, 상품소개, 위치, 안내사항, 취소환불, 파트너정보
  // (리뷰는 탭과 분리되어 BusinessInfoSection 아래 단독 렌더)
  // 콘텐츠가 없는 탭은 표시하지 않음
  const navTabs = useMemo(
    () =>
      [
        hasNotices && {
          id: "notices",
          label: t("tabs.notices"),
        },
        hasBikeSpecTable && {
          id: "bikeSpecs",
          label: t("bikeSpecs.title"),
        },
        hasDescription && {
          id: "description",
          label: t("tabs.description"),
        },
        hasBranches && {
          id: "location",
          label: t("tabs.location"),
        },
        hasAdditionalInfo && {
          id: "info",
          label: t("tabs.info"),
        },
        hasCancellationPolicy && {
          id: "cancellation",
          label: t("tabs.cancellation"),
        },
        { id: "partner", label: t("tabs.partner") },
      ].filter(Boolean) as Array<{
        id: string;
        label: string;
        count?: number;
      }>,
    [
      t,
      hasNotices,
      hasBikeSpecTable,
      hasDescription,
      hasBranches,
      hasAdditionalInfo,
      hasCancellationPolicy,
    ],
  );

  // Scroll to section - memoized callback
  const scrollToSection = useCallback(
    (ref: React.RefObject<HTMLDivElement | null> | undefined) => {
      if (ref?.current) {
        // Account for navbar + sticky tab height (56+48 mobile, 80+48 desktop)
        // + status bar inset (Navbar 는 pt-safe 만큼 더 높다 — edge-to-edge 앱/PWA)
        const isMobile = window.innerWidth < 768;
        const yOffset = (isMobile ? -110 : -135) - readSafeAreaInsetTop();
        const y =
          ref.current.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    },
    [],
  );

  // Recently viewed products
  const { addRecentlyViewed } = useRecentlyViewed();

  // Add product to recently viewed on mount
  useEffect(() => {
    if (productId) {
      addRecentlyViewed(productId);
    }
  }, [productId, addRecentlyViewed]);

  // Track product view (server-side, deduplicated per user per day)
  useEffect(() => {
    if (productId && product?.partner_id) {
      // Fire and forget - don't block UI
      recordProductView({
        productId,
        partnerId: product.partner_id,
      }).catch((err) => {
        // Silent fail - analytics should not affect user experience
        console.warn("Failed to record product view:", err);
      });
      // Funnel: view_item (first-party, session-stitched).
      void recordAnalyticsEvent({
        eventType: "view_item",
        sessionId: getSessionId(),
        metadata: { product_id: productId, partner_id: product.partner_id },
      }).catch(() => {});
    }
  }, [productId, product?.partner_id]);

  // Handle chat inquiry - memoized to prevent child re-renders.
  // 방만 빠르게 생성/조회한 뒤 곧장 채팅방으로 이동하고, 문의 메시지는 방 안에서
  // 옵티미스틱하게 전송한다(`?inquiry=<productId>`). 무거운 메시지 insert+푸시를
  // 이동 전에 기다리지 않아 체감이 빠르다.
  const handleChatClick = useCallback(async () => {
    if (!product?.id || !product?.partner_id) return;

    // 인증이 확인됐고(authChecked) 로그아웃이면 페이지를 유지한 채 AuthModal.
    if (authChecked && !user) {
      setShowAuthModal(true);
      return;
    }

    setIsChatLoading(true);
    try {
      const result = await getOrCreateInquiryRoom(
        product.id,
        product.partner_id,
      );

      if (result.data?.id) {
        // Pass product name + first image so the chat room can render the full
        // product card optimistically (no content change when it reconciles).
        const firstImage = product.images?.length
          ? [...product.images].sort(
              (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
            )[0]?.image_url
          : undefined;
        const params = new URLSearchParams({ inquiry: product.id });
        if (product.name) params.set("pn", product.name);
        if (firstImage) params.set("pi", firstImage);
        router.push(
          `/${locale}/messages/${result.data.id}?${params.toString()}`,
        );
        return;
      }

      // 로딩 타이밍 등으로 미인증이면 AuthModal, 그 외엔 토스트.
      if (result.error === "Please log in") {
        setIsChatLoading(false);
        setShowAuthModal(true);
        return;
      }

      setIsChatLoading(false);
      toast({
        title: t("booking.error") || "오류",
        description:
          result.error ||
          t("booking.chatError") ||
          "채팅방 생성 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } catch (error) {
      console.error("Failed to open inquiry chat:", error);
      setIsChatLoading(false);
      toast({
        title: t("booking.error") || "오류",
        description:
          t("booking.chatError") || "채팅방 생성 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    }
  }, [product, router, locale, user, authChecked, toast, t]);

  // Handle scroll to show/hide sticky nav and update active section
  // Optimized: Uses startTransition for non-urgent updates to prevent jank
  useEffect(() => {
    let ticking = false;
    // Navbar·sticky 탭은 status bar inset 만큼 아래에 붙는다. 매 프레임 측정하지 않도록 한 번만 읽는다.
    const safeTop = readSafeAreaInsetTop();

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const isMobile = window.innerWidth < 768;

        // Calculate new values
        let newShowStickyNav = showStickyNav;
        let newActiveSection = activeSection;

        // Show sticky nav only when 2nd section's top has scrolled past navbar
        // (review is 1st section now; use notices when present, else description as fallback)
        const triggerEl = noticesRef.current ?? descriptionRef.current;
        if (triggerEl) {
          const triggerRect = triggerEl.getBoundingClientRect();
          const navbarHeight = (isMobile ? 56 : 80) + safeTop;
          newShowStickyNav = triggerRect.top <= navbarHeight;
        }

        // Update active section based on scroll position
        // Order must match navTabs: notices → bikeSpecs → description → location → info → cancellation → partner
        // (review is rendered standalone above the tabs and excluded from scroll-spy)
        const sections = [
          { id: "notices", ref: noticesRef },
          { id: "bikeSpecs", ref: bikeSpecsRef },
          { id: "description", ref: descriptionRef },
          { id: "location", ref: locationRef },
          { id: "info", ref: infoRef },
          { id: "cancellation", ref: cancellationRef },
          { id: "partner", ref: hostInfoDetailRef },
        ];

        const sectionOffset = (isMobile ? 120 : 150) + safeTop;

        for (let i = sections.length - 1; i >= 0; i--) {
          const section = sections[i];
          if (section.ref.current) {
            const rect = section.ref.current.getBoundingClientRect();
            if (rect.top <= sectionOffset) {
              newActiveSection = section.id;
              break;
            }
          }
        }

        // Batch state updates using startTransition for non-urgent UI updates
        startTransition(() => {
          if (newShowStickyNav !== showStickyNav) {
            setShowStickyNav(newShowStickyNav);
          }
          if (newActiveSection !== activeSection) {
            setActiveSection(newActiveSection);
          }
        });

        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showStickyNav, activeSection]);

  // 활성 탭이 화면에 보이도록 자동 스크롤
  useEffect(() => {
    const activeTab = tabRefs.current.get(activeSection);
    const container = tabContainerRef.current;

    if (activeTab && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();

      // 탭이 컨테이너 왼쪽이나 오른쪽으로 벗어난 경우 스크롤
      const isOutOfViewLeft = tabRect.left < containerRect.left;
      const isOutOfViewRight = tabRect.right > containerRect.right;

      if (isOutOfViewLeft || isOutOfViewRight) {
        // 탭을 중앙에 배치하도록 스크롤
        const scrollLeft =
          activeTab.offsetLeft -
          container.offsetWidth / 2 +
          activeTab.offsetWidth / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }
  }, [activeSection]);

  // Determine pricing mode from product.rental_unit (DB에서 관리)
  const pricingMode = useMemo(() => {
    // rental_unit 값 사용: 'timeslot' → timeslot 모드, 'daily' → daily 모드
    if (product?.rental_unit === "timeslot") {
      return "timeslot";
    }
    return "daily";
  }, [product.rental_unit]);

  // Get base daily rate from pricing (min_days = 1) for display purposes
  const baseDailyRate = useMemo(() => {
    return getBaseDailyRate(product?.pricing || []);
  }, [product.pricing]);

  // Timeslot v2 매트릭스 언어 병합 (Phase B-2 — 표시 전용, 예약 UI 분기는 B-3).
  // 폴백 원본 언어는 PricingInfo v1 관례와 동일하게 ko.
  const timeslotMatrix = useMemo(() => {
    if (product.timeslot_version !== 2) return null;
    return mergeTimeslotMatrixConfig(
      {
        timeOptionRows: product.time_options || [],
        subProductRows: product.sub_products || [],
        priceRows: product.sub_product_prices || [],
      },
      locale,
      "ko",
    );
  }, [
    product.timeslot_version,
    product.time_options,
    product.sub_products,
    product.sub_product_prices,
    locale,
  ]);

  // 일일대여 v2 세부 상품 병합 (08-17) — 세부 상품마다 독립 기간 티어(결정 2).
  // 폴백 원본 언어는 타임슬롯 v2와 동일하게 ko.
  const dailyMatrix = useMemo(() => {
    if (product.daily_version !== 2) return null;
    return mergeDailyMatrixConfig(
      {
        subProductRows: product.sub_products || [],
        dayPriceRows: product.day_prices || [],
      },
      locale,
      "ko",
    );
  }, [product.daily_version, product.sub_products, product.day_prices, locale]);

  // Get timeslots for timeslot mode
  // 현재 locale에 맞는 pricing만 필터링하고 표시 순서(sort_order)로 정렬
  const timeSlots = useMemo(() => {
    if (!product?.pricing || product.pricing.length === 0) {
      return [];
    }

    // 현재 언어의 timeslot pricing 필터링
    let localePricing = product.pricing.filter(
      (p) => p.pricing_type === "timeslot" && p.lang_code === locale,
    );

    // 현재 언어에 데이터가 없으면 기본 언어(ko) 사용
    if (localePricing.length === 0) {
      localePricing = product.pricing.filter(
        (p) =>
          p.pricing_type === "timeslot" &&
          (p.lang_code === "ko" || !p.lang_code),
      );
    }

    // 08-11 Phase 2: 정렬은 **표시 순서(sort_order)** — 없으면 display_order 폴백
    // (백필 이전 행 안전). display_order는 아래 displayOrder로 계속 내려보내는
    // **식별 좌표**(대여 제한·매진 매칭)라 정렬과 역할이 분리됐다.
    return localePricing
      .sort(
        (a, b) =>
          (a.sort_order ?? a.display_order ?? 0) -
          (b.sort_order ?? b.display_order ?? 0),
      )
      .map((p) => ({
        id: p.id || "",
        // duration_hours는 null일 수 있음 (label-only 슬롯)
        durationHours: p.duration_hours ?? undefined,
        price: p.price || 0,
        label: p.label,
        // display_order: language-independent slot identity used by per-slot
        // operation blocks (timeslot_orders).
        displayOrder: p.display_order ?? 0,
      }));
  }, [product.pricing, locale]);

  // Calculate total price for cart items with tiered pricing
  const totalPrice = useMemo(() => {
    const pricing = product?.pricing || [];
    return cartItems.reduce((total, item) => {
      if (item.type === "rental") {
        if (item.pickupDate && item.returnDate) {
          const days = calculateRentalDays(item.pickupDate, item.returnDate);
          return total + calculateRentalPrice(days, item.quantity, pricing);
        } else if (item.timeSlotPrice) {
          return total + item.timeSlotPrice * item.quantity;
        }
      } else if (item.type === "option") {
        return total + (item.optionPrice || 0) * item.quantity;
      }
      return total;
    }, 0);
  }, [cartItems, product.pricing]);

  // Memoize sorted images to prevent unnecessary array operations.
  // Hook must be called before any early return to satisfy rules-of-hooks.
  const images = useMemo(() => {
    if (!product?.images || product.images.length === 0) return [];
    return [...product.images].sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
    );
  }, [product.images]);

  // Data is already loaded from server, no need to fetch again

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <div className="flex items-center justify-center h-screen">
          <p>{t("notFound")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <div>
        {/* Inactive Product Warning Banner */}
        {product.status === "inactive" && (
          <div className="bg-amber-50 border-b border-amber-200">
            <div className="px-6 sm:px-10 md:px-16 lg:px-20 xl:px-24 max-w-7xl mx-auto">
              <div className="flex items-center gap-3 py-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800 font-medium">
                  {t("inactiveProductWarning")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Desktop only (Above Image Gallery) */}
        <div className="hidden md:block px-6 sm:px-10 md:px-16 lg:px-20 xl:px-24 max-w-7xl mx-auto">
          <div className="flex items-center justify-end gap-2 py-3">
            <Button
              variant="ghost"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-all duration-200"
              aria-label={t("share.title")}
              onClick={() => setShareDialogOpen(true)}
            >
              <ShareNetworkIcon className="w-5 h-5" />
            </Button>
            <WishlistButton
              productId={productId}
              size="md"
              variant="default"
              onAuthRequired={() => setShowAuthModal(true)}
              initialWishlisted={isWishlisted}
              initialAuthenticated={!!user}
              onWishlistChange={setIsWishlisted}
            />
          </div>
        </div>

        {/* Image Gallery with Mobile Overlay Buttons */}
        <div className="mb-6 md:mb-12 relative">
          <ProductImageGallery images={images} productName={product.name} />

          {/* Mobile Action Buttons - Overlay on Image Gallery */}
          <div className="md:hidden absolute top-3 right-3 flex items-center gap-2 z-10">
            <button
              className="w-8 h-8 rounded-full flex items-center justify-center bg-white/80 hover:bg-white backdrop-blur-sm transition-all duration-200"
              aria-label={t("share.title")}
              onClick={() => setShareDialogOpen(true)}
            >
              <ShareNetworkIcon className="w-4 h-4 text-gray-600" />
            </button>
            <WishlistButton
              productId={productId}
              size="sm"
              variant="overlay"
              onAuthRequired={() => setShowAuthModal(true)}
              initialWishlisted={isWishlisted}
              initialAuthenticated={!!user}
              onWishlistChange={setIsWishlisted}
            />
          </div>
        </div>

        {/* Content Grid */}
        <div className="px-4 sm:px-10 md:px-16 lg:px-20 xl:px-24 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-8 lg:gap-12 pb-12">
          {/* Left Column - Details */}
          <div className="min-w-0 space-y-6">
            {/* Breadcrumb · partner · title · reviews — one block for both
                breakpoints (one <h1>). The breadcrumb sits right under the
                gallery on mobile and above the title on desktop. */}
            <div className="max-w-7xl mx-auto mb-5 pb-4 border-b md:mb-6 md:pb-5">
              {contextLinks && (
                <ProductBreadcrumbs
                  items={contextLinks.trail}
                  label={t("contextLinks.breadcrumbLabel")}
                  className="mb-2"
                />
              )}

              {/* Partner & Location Info */}
              {(product.partner?.name || hasRegionLine) && (
                <div className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm leading-5 text-gray-600">
                  <Link
                    href={`/partner/${product.partner?.slug}`}
                    className="flex items-center gap-1.5 hover:text-gray-900 transition-colors"
                  >
                    {product.partner?.logo_url && (
                      <OptimizedImage
                        src={product.partner.logo_url}
                        alt={product.partner?.name || ""}
                        width={20}
                        height={20}
                        className="rounded-md object-cover w-5 h-5 ring-1 ring-gray-300"
                      />
                    )}
                    {product.partner?.name && (
                      <span className="font-medium">
                        {product.partner.name}
                      </span>
                    )}
                  </Link>
                  {product.partner?.name && hasRegionLine && <span>•</span>}
                  {hasRegionLine && (
                    <span>
                      {regionLine.shown.map((city, index) => (
                        <span key={city.name}>
                          {index > 0 && ", "}
                          {city.href ? (
                            <NextLink
                              href={city.href}
                              className="text-gray-700 underline decoration-gray-300 underline-offset-[3px] hover:text-gray-900 hover:decoration-gray-500"
                            >
                              {city.name}
                            </NextLink>
                          ) : (
                            city.name
                          )}
                        </span>
                      ))}
                      {regionLine.more && ` ${regionLine.more}`}
                    </span>
                  )}
                </div>
              )}

              {/* Title */}
              <h1 className="mb-2 wrap-break-word text-xl leading-snug font-semibold text-gray-900 md:text-2xl md:leading-8">
                {product.name}
              </h1>

              {/* Meta Info - 리뷰 정보만 표시 */}
              <div className="flex items-center gap-4 flex-wrap text-sm">
                {initialReviewsTotal > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-current text-orange-400" />
                    <span className="font-semibold">
                      {initialReviewStats.averageRating}
                    </span>
                    <span className="text-gray-600">
                      (
                      {t("reviews.count", {
                        count: initialReviewsTotal,
                      })}
                      )
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing Info */}
            <PricingInfo
              pricing={product.pricing}
              rentalUnit={pricingMode}
              currency={sourceCurrency}
              locale={locale}
              matrix={timeslotMatrix}
              dailyMatrix={dailyMatrix}
              productName={product.name}
              pricingSource={product.pricingSource}
              seasons={product.pricing_seasons}
            />

            {/* 매장 정보 모달 */}
            <BusinessInfoModal
              branches={product.branches || []}
              usageMethods={product.usage_methods || []}
              open={businessInfoModalOpen}
              onOpenChange={setBusinessInfoModalOpen}
              partnerCountryCode={product.partner?.country_code}
            />

            {/* Business Info Section - 지점/이용방식 미리보기 */}
            <BusinessInfoSection
              branches={product.branches || []}
              usageMethods={product.usage_methods || []}
              onViewDetails={() => {
                setBusinessInfoModalOpen(true);
              }}
              partnerCountryCode={product.partner?.country_code}
            />

            {/* Review Section (standalone, not part of sticky tab navigation) */}
            {/* 리뷰 목록은 캐시된 HTML 에 넣지 않는다 — 보는 사람의 차단 목록(user_blocks,
                iOS 1.2)으로 걸러야 하므로 마운트 후 서버 액션으로 받는다. 집계(평점·개수)만
                서버에서 내려온다. */}
            <ProductReviews
              productId={productId}
              partnerId={product.partner_id}
              currentUserId={user?.id}
              viewerResolved={authChecked}
              onAuthRequired={() => setShowAuthModal(true)}
              initialStats={initialReviewStats}
              initialTotal={initialReviewsTotal}
            />

            {/* Sticky Tab Navigation - Underline style like test page */}
            {/* Navbar(sticky, pt-safe) 바로 아래 → nav 높이 + status bar inset */}
            <div className="sticky top-[calc(3rem+var(--loc-safe-area-inset-top))] md:top-[calc(5rem+var(--loc-safe-area-inset-top))] z-30 bg-white border-t border-b">
              <div
                ref={tabContainerRef}
                className="flex whitespace-nowrap overflow-x-auto scrollbar-hide"
              >
                {navTabs.map((tab) => (
                  <button
                    key={tab.id}
                    ref={(el) => {
                      if (el) tabRefs.current.set(tab.id, el);
                    }}
                    onClick={() => scrollToSection(sectionRefs[tab.id])}
                    className={cn(
                      "py-3 text-sm font-medium border-b-2 transition-colors shrink-0 px-4",
                      activeSection === tab.id
                        ? "border-gray-900 text-gray-900"
                        : "border-transparent text-gray-500 hover:text-gray-700",
                    )}
                  >
                    {tab.label}
                    {tab.count !== undefined && tab.count > 0 && (
                      <span className="ml-1 text-xs text-gray-400">
                        ({tab.count.toLocaleString()})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* 1. Notices - 공지사항 */}
            <div ref={noticesRef}>
              <ProductNotices
                partnerNotices={product.partner_notices}
                productNotices={product.product_notices}
                partnerName={product.partner?.name}
                partnerIcon={product.partner?.logo_url}
              />
            </div>

            {/* 2. Vehicle Spec Table - 차량 정보 (오토바이 상품 전용) */}
            {hasBikeSpecTable && (
              <div ref={bikeSpecsRef}>
                <BikeSpecTable
                  specifications={product.specifications}
                  extraRows={keyFactRows}
                  modelSpecs={modelSpecs}
                  modelName={bikeModelName}
                />
              </div>
            )}

            {/* 핵심 정보 — 오토바이가 아닌 상품(캠핑 세트·폰·카메라·뷰티)의 사실 2~4줄 */}
            {!hasBikeSpecTable && (
              <ProductKeyFacts title={tKeyFacts("title")} rows={keyFactRows} />
            )}

            {/* 3. Description Section - 상품소개 (상세 이미지 통합) */}
            <div ref={descriptionRef} className="space-y-4">
              {/* 쿠폰 배너 - 소개 섹션 최상단 */}
              <ProductCouponBanner
                productId={productId}
                initialCoupons={initialCoupons}
                onAuthRequired={() => setShowAuthModal(true)}
              />
              <ProductDescription
                content={product.description || ""}
                sections={product.detail_sections}
                detailPageHtml={product.detail_page_html}
                detailPageBody={detailPageBody}
                productName={product.name}
                componentSections={componentSections}
              />
            </div>

            {/* 4. Location Section - 위치 (Branches with Map) */}
            <div ref={locationRef}>
              <ProductLocationHeader
                branches={product.branches || []}
                categoryCode={product.category_code}
                cityLandings={contextLinks?.cityLandings ?? []}
                stations={contextLinks?.stations ?? []}
                model={contextLinks?.model ?? null}
              />
              <ProductBranches
                branches={product.branches || []}
                showMap={true}
                categoryCode={product.category_code}
                bigMapHref={bigMapHref}
                hideHeading
              />
            </div>

            {/* 5. Info Section - 안내사항 (Additional Info + FAQs) */}
            <div ref={infoRef}>
              <ProductAdditionalInfo
                additionalInfo={product.additional_info}
                inclusions={product.inclusions}
                exclusions={product.exclusions}
                notes={product.notes}
                requirements={product.requirements}
              />
              <ProductFAQs faqs={product.faqs || []} />
            </div>

            {/* 6. Cancellation Policy Section - 취소환불 */}
            <div ref={cancellationRef}>
              <CancellationPolicy
                policy={product.partner?.cancellation_refund_policy}
              />
            </div>

            {/* 7. Partner Info Detail - 파트너정보 */}
            <div ref={hostInfoDetailRef}>
              <PartnerInfoDetail
                partner={product.partner}
                primaryCity={product.primary_city || undefined}
              />
            </div>
          </div>

          {/* Right Column - Booking Card (Desktop Only) */}
          <div className="hidden lg:block">
            <div
              ref={bookingCardRef}
              className="p-6 border rounded-3xl shadow-xl bg-white"
              style={bookingCardStyle}
            >
              <QueryAwareBookingCard
                product={product}
                locale={locale}
                timeOptions={timeOptions}
                pricingMode={pricingMode}
                dailyRate={baseDailyRate}
                timeSlots={timeSlots}
                timeslotMatrix={timeslotMatrix}
                dailyMatrix={dailyMatrix}
                cartItems={cartItems}
                setCartItems={setCartItems}
                productBlocks={productBlocks}
                branchBlocks={branchBlocks}
                onAuthRequired={() => setShowAuthModal(true)}
                isAuthenticated={!!user}
                onChatClick={handleChatClick}
                isChatLoading={isChatLoading}
                isWishlisted={isWishlisted}
                onWishlistChange={setIsWishlisted}
              />
            </div>
          </div>
        </div>

        {/* 추천 상품 섹션 - 전체 폭 */}
        <div className="px-4 sm:px-10 md:px-16 lg:px-20 xl:px-24 max-w-7xl mx-auto pb-12">
          {product.category_code && (
            <SimilarCategoryProducts
              categoryId={product.category_code}
              excludeProductIds={[product.id]}
              locale={locale}
            />
          )}
          {product.partner_id && (
            <PartnerProducts
              partnerId={product.partner_id}
              partnerName={product.partner?.name}
              partnerSlug={product.partner?.slug}
              excludeProductIds={[product.id]}
              locale={locale}
            />
          )}
        </div>
      </div>

      {/* Mobile Booking Drawer - now handled by BookingCard variant="mobile" */}
      <QueryAwareBookingCard
        product={product}
        locale={locale}
        timeOptions={timeOptions}
        pricingMode={pricingMode}
        dailyRate={baseDailyRate}
        timeSlots={timeSlots}
        timeslotMatrix={timeslotMatrix}
        dailyMatrix={dailyMatrix}
        variant="mobile"
        cartItems={cartItems}
        setCartItems={setCartItems}
        productBlocks={productBlocks}
        branchBlocks={branchBlocks}
        onAuthRequired={() => setShowAuthModal(true)}
        isAuthenticated={!!user}
        onChatClick={handleChatClick}
        isChatLoading={isChatLoading}
        isWishlisted={isWishlisted}
        onWishlistChange={setIsWishlisted}
        mobileFloatingActions={
          <ProductFloatingActions
            bigMapHref={bigMapHref}
            onShare={() => setShareDialogOpen(true)}
          />
        }
      />

      {/* 데스크톱 원형 버튼 묶음 — 화면 오른쪽 아래 고정.
          모바일은 하단 바 위(MobileBookingDrawer)에 붙는다. */}
      <div className="hidden lg:block">
        <ProductFloatingActions
          variant="desktop"
          bigMapHref={bigMapHref}
          onShare={() => setShareDialogOpen(true)}
        />
      </div>

      {/* Auth Modal */}
      <AuthModal
        open={showAuthModal}
        onOpenChange={(open) => {
          setShowAuthModal(open);
          // Recheck user when modal closes
          if (!open) {
            void refreshAuth();
          }
        }}
      />

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        product={{
          id: product.id,
          name: product.name,
          description: product.description,
          images: product.images,
        }}
        locale={locale}
      />
    </div>
  );
}
